import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  Cog8ToothIcon,
  BoltIcon,
  UserGroupIcon,
  ChartBarIcon,
  RectangleStackIcon,
  PuzzlePieceIcon,
  AcademicCapIcon,
  BeakerIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  SparklesIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const AIDashboard = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, healthResponse] = await Promise.all([
        axiosInstance.get('/api/ai/stats'),
        axiosInstance.get('/api/ai/health')
      ]);

      setStats(statsResponse.data.data);
      setHealth(healthResponse.data.data);
    } catch (error) {
      logger.error('Error fetching dashboard data:', error);
      showNotification?.('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [statsResponse, healthResponse] = await Promise.all([
        axiosInstance.get('/api/ai/stats'),
        axiosInstance.get('/api/ai/health')
      ]);
      setStats(statsResponse.data.data);
      setHealth(healthResponse.data.data);
    } catch (error) {
      showNotification?.('Failed to refresh', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const menuItems = [
    {
      title: 'Configuration',
      description: 'Main AI assistant and behavior settings',
      icon: Cog8ToothIcon,
      path: '/ai-configuration',
      iconBg: 'bg-gray-100 dark:bg-gray-800/50',
      iconColor: 'text-gray-600 dark:text-gray-400'
    },
    {
      title: 'Rules',
      description: 'If/then automation and delegation',
      icon: BoltIcon,
      path: '/ai-rules',
      count: stats?.rules?.count || 0,
      iconBg: 'bg-yellow-50 dark:bg-yellow-900/20',
      iconColor: 'text-yellow-600 dark:text-yellow-400'
    },
    {
      title: 'Workflows',
      description: 'Multi-step automated processes',
      icon: RectangleStackIcon,
      path: '/ai-workflows',
      count: stats?.workflows?.count || 0,
      iconBg: 'bg-blue-50 dark:bg-blue-900/20',
      iconColor: 'text-blue-600 dark:text-blue-400'
    },
    {
      title: 'Department Agents',
      description: 'Specialized AI for HR, Finance, IT',
      icon: UserGroupIcon,
      path: '/ai-agents',
      count: stats?.agents?.count || 0,
      iconBg: 'bg-purple-50 dark:bg-purple-900/20',
      iconColor: 'text-purple-600 dark:text-purple-400'
    },
    // {
    //   title: 'Integrations',
    //   description: 'Connect external services',
    //   icon: PuzzlePieceIcon,
    //   path: '/ai-integrations',
    //   count: stats?.integrations?.count || 0,
    //   iconBg: 'bg-green-50 dark:bg-green-900/20',
    //   iconColor: 'text-green-600 dark:text-green-400'
    // },
    {
      title: 'Knowledge Base',
      description: 'Company policies, SOPs, and FAQs',
      icon: AcademicCapIcon,
      path: '/ai-knowledge',
      count: stats?.knowledge?.count || 0,
      iconBg: 'bg-indigo-50 dark:bg-indigo-900/20',
      iconColor: 'text-indigo-600 dark:text-indigo-400'
    },
    {
      title: 'Templates',
      description: 'Email, contract, and document templates',
      icon: DocumentTextIcon,
      path: '/ai-templates',
      count: stats?.templates?.count || 0,
      iconBg: 'bg-cyan-50 dark:bg-cyan-900/20',
      iconColor: 'text-cyan-600 dark:text-cyan-400'
    },
    {
      title: 'Analytics',
      description: 'Performance metrics and insights',
      icon: ChartBarIcon,
      path: '/ai-analytics',
      iconBg: 'bg-pink-50 dark:bg-pink-900/20',
      iconColor: 'text-pink-600 dark:text-pink-400'
    },
    {
      title: 'Testing',
      description: 'Test rules and simulate workflows',
      icon: BeakerIcon,
      path: '/ai-testing',
      iconBg: 'bg-orange-50 dark:bg-orange-900/20',
      iconColor: 'text-orange-600 dark:text-orange-400'
    }
  ];

  const getHealthStatus = () => {
    if (!health?.components) return { dotColor: 'bg-gray-400', text: 'Unknown' };

    const components = Object.values(health.components);
    const hasError = components.some(c => c === 'error');
    const hasWarning = components.some(c => c?.includes('no_') || c === 'not_configured');

    if (hasError) return { dotColor: 'bg-red-500', text: 'Needs Attention' };
    if (hasWarning) return { dotColor: 'bg-yellow-500', text: 'Partially Configured' };
    return { dotColor: 'bg-green-500 animate-pulse', text: 'All Systems Operational' };
  };

  const healthStatus = getHealthStatus();

  const statCards = [
    { label: 'Active Rules', value: stats?.rules?.count || 0 },
    { label: 'Department Agents', value: stats?.agents?.count || 0 },
    { label: 'Workflows', value: stats?.workflows?.count || 0 },
    { label: 'Knowledge Items', value: stats?.knowledge?.count || 0 },
  ];

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#111111]">
          <div className="h-5 bg-gray-200 dark:bg-[#222222] rounded w-32 animate-pulse"></div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-6">
          <div className="animate-pulse space-y-6 max-w-5xl mx-auto">
            <div className="grid grid-cols-4 gap-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-16 bg-gray-200 dark:bg-[#1a1a1a] rounded-xl"></div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="h-20 bg-gray-200 dark:bg-[#1a1a1a] rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#111111]">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-900 dark:bg-white rounded-xl">
                <SparklesIcon className="w-5 h-5 text-white dark:text-gray-900" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Iris AI
                </h1>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Autonomous business operations platform
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              {/* Health status */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800">
                <span className={`w-2 h-2 rounded-full ${healthStatus.dotColor}`}></span>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {healthStatus.text}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
          {/* Getting Started */}
          {stats?.configuration?.status === 'not_configured' && (
            <div className="flex items-center gap-4 p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10">
              <ExclamationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Get started with Iris AI</p>
                <p className="text-xs text-blue-700 dark:text-blue-300">Configure your AI settings to start automating.</p>
              </div>
              <button
                onClick={() => navigate('/ai-configuration')}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
              >
                Configure
              </button>
            </div>
          )}

          {/* Quick Stats */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {statCards.map((stat, i) => (
                <div key={i} className="px-4 py-3 bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Menu Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="group bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl p-4 hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-sm transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <div className={`flex-shrink-0 p-2 rounded-lg ${item.iconBg}`}>
                    <item.icon className={`w-5 h-5 ${item.iconColor}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.title}
                      </h3>
                      {item.count !== undefined && item.count > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-[#222222] text-gray-600 dark:text-gray-400">
                          {item.count}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {item.description}
                    </p>
                  </div>

                  <ArrowRightIcon className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIDashboard;
